/**
 * Cliente del endpoint /api/v1/me del backend.
 * Doc 12 (auth) + Doc 19 (i18n).
 */

import { apiFetch } from './client'
import type { TenantAccess, TenantStatus } from './platform'
import type { SupportedLocale } from '../i18n/config'

export interface TenantSummary {
  id: string
  slug: string
  display_name: string
  /** i18n — fallback para users/projects nuevos del tenant. Doc 19 §2.3. */
  default_locale: string
  /** Estado comercial (OT-0072). */
  status: TenantStatus
  trial_ends_at: string | null
  /** Lo calcula el backend (`permissions.tenant_access`); la UI no lo deduce
   * de `status` + `trial_ends_at` (OT-0073 §4.4). */
  access: TenantAccess
  /** Contacto de soporte de HIVA Nexus (`Settings.support_email`), que la UI
   * muestra con el estado de la organización (OT-0073 §4.4); `null` si el
   * ambiente no lo configura. */
  support_email: string | null
}

export type UserStatus = 'active' | 'pending' | 'disabled'
export type UserLocale = 'es' | 'en'

/** **Tier de licencia** del usuario a nivel tenant (OT-0008 §4.2).
 *
 * Es el eje de permisos: define el techo de lo que la persona puede
 * hacer en cualquier parte del tenant, y determina el costo del asiento.
 *
 * Es **ortogonal al rol funcional** dentro de un proyecto, que vive en
 * `project_members.role` (template_key del catálogo de Role Templates —
 * Doc 06). El rol funcional NO otorga permisos: alguien con rol
 * "Analista" y tier `reviewer` no puede editar nada.
 *
 *  · `admin`    — todo en el tenant y en todos sus proyectos.
 *  · `editor`   — produce contenido donde está asignado. Único tier
 *                 (con admin) que puede ser dueño de un artefacto.
 *  · `reviewer` — lee el proyecto completo y aprueba, pero no produce.
 *  · `external` — stakeholder del cliente: solo ve lo que aprueba.
 *
 * Backend enum: `app/models/user.py UserTier`. Los valores previos
 * (`tenant_admin`/`member`/`client`/`auditor`) los migró la 0084. */
export type UserTenantRole = 'admin' | 'editor' | 'reviewer' | 'external'

export interface MeResponse {
  /** HIVA Nexus internal user id (UUID) */
  id: string
  /** Object ID del usuario en Entra (claim 'oid') */
  entra_oid: string
  email: string
  display_name: string
  /** Tipo de cuenta del user en el tenant. */
  role: UserTenantRole
  status: UserStatus
  /** i18n — preferencia UI del usuario. Source of truth post-auth. Doc 19 §2.1. */
  locale: UserLocale
  /** null cuando el usuario está en limbo (autenticado pero sin tenant) */
  tenant: TenantSummary | null
  /** Doc 25 §8 — true si el user tiene acceso cross-tenant via
   *  `/platform/*`. La sidebar muestra el item "Plataforma" solo
   *  cuando este flag es true. Backend lo expone en /me; ver
   *  hivanexus-backend/app/api/v1/me.py. */
  is_platform_admin?: boolean
  /** OT-0008 §4.2.1 (D19) — lectura transversal. Extiende la
   *  VISIBILIDAD a todos los proyectos del tenant sin conceder
   *  capacidad: lo que puede hacer sigue acotado por su tier.
   *
   *  Se combina con la membresía por UNIÓN, nunca como override. En los
   *  proyectos donde SÍ es miembro conserva todo lo de su tier; en los
   *  demás es solo lectura (no comenta, no puede ser aprobador).
   *
   *  Prohibido en tier `external` — lo enforce un CHECK en BD. */
  can_view_all_projects?: boolean
}

export async function fetchMe(accessToken: string): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/v1/me', { accessToken })
}

/** Persiste el cambio de idioma del usuario. El backend valida 'es' | 'en'
 * (Doc 19 §2.1) y lo guarda en `users.locale`. */
export async function patchMeLocale(
  accessToken: string,
  locale: UserLocale,
): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/v1/me', {
    accessToken,
    method: 'PATCH',
    body: { locale },
  })
}

// ─── /me/inbox (B.5.d.2) ────────────────────────────────────────────────────

/**
 * Aprobación pendiente asignada al usuario, contextualizada con la
 * versión del artifact y el artifact padre. Shape exacto del backend
 * `InboxApprovalItem` en app/api/v1/me.py.
 */
export interface InboxApprovalItem {
  approval_id: string
  artifact_id: string
  artifact_version_id: string
  artifact_name: string
  artifact_code: string | null
  project_id: string
  template_key: string
  version_number: number
  approver_role: string
  /** Stage donde vive esta approval. Doc 21 (Approval Workflow). En
   *  artifacts pre-Doc 21 (legacy single-stage) viene null. */
  stage_order?: number | null
  /** Nombre del stage en es+en (snapshot al submit). Null en legacy. */
  stage_name?: Partial<Record<SupportedLocale, string>> | null
  /** ISO datetime */
  created_at: string
}

export interface InboxCounts {
  approvals: number
  changes: number
  drafts: number
}

export interface InboxResponse {
  approvals: InboxApprovalItem[]
  /** Vacío hasta Fase E (change_requests). */
  changes: unknown[]
  /** Vacío hasta B.6 (drafts cross-project). */
  drafts: unknown[]
  counts: InboxCounts
}

/** Bandeja personal del usuario para el workspace home. Cross-project
 * intra-tenant (RLS lo limita al tenant activo). */
export async function fetchMyInbox(
  accessToken: string,
): Promise<InboxResponse> {
  return apiFetch<InboxResponse>('/api/v1/me/inbox', { accessToken })
}
